'use client'

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Menu } from "lucide-react"

import Logo from "@/components/Logo"
import UserAvatar from "@/components/elements/UserAvatar"
import { Button } from "@/components/elements/Button"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"

import { cn } from "@/lib/util"
import type { SiteUser } from "@/lib/site"

const siteLinks = [
  { href: '/#features', label: 'Боломжууд' },
  { href: '/#developers', label: 'Хөгжүүлэгчдэд' },
  { href: '/pricing', label: 'Үнэ' },
  { href: '/docs', label: 'API заавар' },
]

// The header of the public site: a pane of glass that floats over the page, so whatever scrolls
// under it shows through, blurred. A visitor sees the ways in; a signed-in user sees themselves,
// and one click takes them to the dashboard. Pages leave room for it at their top.
export default function SiteHeader({ user }: { user: SiteUser | null }) {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-40 px-3 pt-3 sm:px-6">
      <div className={cn("pointer-events-auto mx-auto flex h-14 max-w-7xl items-center gap-2 rounded-2xl border px-2 backdrop-blur-xl backdrop-saturate-150 transition-[background-color,border-color,box-shadow] duration-300 sm:gap-3 sm:px-4",
        "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.5)] dark:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.08)]",
        scrolled
          ? 'border-black/10 bg-background/70 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.5),0_8px_30px_-12px_rgb(0_0_0/0.25)] dark:border-white/10 dark:shadow-[inset_0_1px_0_0_rgb(255_255_255/0.08),0_8px_30px_-12px_rgb(0_0_0/0.8)]'
          : 'border-black/5 bg-background/40 dark:border-white/[0.07]')}>
        <Link href="/" aria-label="WeSend" className="flex shrink-0 items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span className="hidden sm:block"><Logo className="h-7 w-auto" /></span>
          <span className="pl-1 sm:hidden"><Logo variant="icon" className="size-8" /></span>
        </Link>

        <nav aria-label="Үндсэн цэс" className="ml-6 hidden items-center gap-1 md:flex">
          {siteLinks.map(link => (
            <Link key={link.href} href={link.href} aria-current={pathname === link.href ? 'page' : undefined}
              className={cn("rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                pathname === link.href ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')}>
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
          {user ? (
            <Link href="/dashboard" prefetch={false} aria-label={`${user.fullName}, самбар руу очих`}
              className="flex h-10 items-center gap-2 rounded-full border border-input bg-background py-1 pl-1 pr-3.5 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <UserAvatar avatar={user.avatar} seed={user.seed} size={30} />
              <span className="max-w-36 truncate">{user.fullName}</span>
            </Link>
          ) : (
            <>
              <Button tag="link" href="/login" variant="ghost" className="max-sm:h-9 max-sm:px-3">Нэвтрэх</Button>
              <Button tag="link" href="/register" className="max-sm:h-9 max-sm:px-3">Бүртгүүлэх</Button>
            </>
          )}

          <button type="button" onClick={() => setOpen(true)} aria-label="Цэс нээх"
            className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden">
            <Menu className="size-5" />
          </button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-72 gap-0">
          <SheetHeader className="border-b">
            <SheetTitle className="font-gilroy">Цэс</SheetTitle>
            <SheetDescription className="sr-only">WeSend сайтын хуудсууд</SheetDescription>
          </SheetHeader>
          <nav aria-label="Үндсэн цэс" className="flex flex-col gap-0.5 p-3">
            {siteLinks.map(link => (
              <Link key={link.href} href={link.href} onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                {link.label}
              </Link>
            ))}
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  )
}
